/**
 * Columns, cell values and the row menu of the overview grid (masterplan credit-limits-v3, A5, A7; E2, E15, E16, E19,
 * E30). B4 adds the optional cell edit of the limit column (`LimitEditColumn`). C6: the limit column shows the effective
 * limit the API sends and marks one written to NAV that the nightly run has not confirmed yet; «Last request» names that
 * write when no request is open. C6a (E42): after the first nightly run a different nightly value is the limit and the
 * cell names the request it differs from. Pure apart from the JSX of a few cells, so the tests read values and static markup without a browser.
 */
import type { ReactNode } from 'react';
import { AlertOctagon, AlertTriangle } from 'lucide-react';
import type { ClimColumn } from '../grid/ClimGrid';
import { LimitCellView } from './LimitCell';
import type { ClimMenuItem } from '../grid/ClimRowMenu';
import { DEFAULT_NEAR_LIMIT_PCT, type LimitBand, type OverviewRow } from '../../../types/creditLimits';
import { BAND_TEXT, meterPercent, previewBand, utilizationBand } from './overviewFilters';

/** Whole numbers in the Swiss grouping; a missing value is a dash. */
export function formatChf(value: number | null | undefined): string {
    if (value === null || value === undefined || !Number.isFinite(value)) return '–';
    return Math.round(value).toLocaleString('de-CH');
}

/** A ratio (0.734) as «73.4 %»; null = no limit. */
export function formatPercent(ratio: number | null | undefined): string {
    if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return '–';
    return `${(ratio * 100).toLocaleString('de-CH', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;
}

/** The ratio as a percentage figure for sorting and the funnel (73.4); null stays null. */
export function percentValue(ratio: number | null): number | null {
    return ratio === null ? null : Math.round(ratio * 1000) / 10;
}

const REQUEST_STATUS: Record<string, string> = {
    pending: 'In approval',
    ready: 'Ready for NAV',
    writing: 'Writing to NAV',
};

const REQUEST_ROLE: Record<string, string> = {
    SM: 'salesperson',
    BTM: 'BT manager',
    TOP: 'top level',
    FIN: 'finance',
};

/** A UTC instant as the Swiss calendar date «27.10.2026». */
export function formatSwissDate(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('de-CH', { timeZone: 'Europe/Zurich', day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * C6: the hint of a limit written into NAV (or entered there by hand) that the nightly run has not confirmed yet, with
 * the value the nightly run still carries; null when nothing is pending.
 */
export function pendingNavWriteText(row: OverviewRow): string | null {
    const p = row.pendingNavWrite;
    if (!p) return null;
    const how = p.mode === 'manual' ? 'Entered in NAV by hand' : 'Written to NAV';
    return `${how} on ${formatSwissDate(p.writtenAt)}, not yet confirmed by the nightly run (request #${p.requestId}; the nightly run still shows ${limitText(row.nightlyLimitChf)}).`;
}

/** «CHF 10'000» or «no limit» (<= 1 or missing). */
function limitText(value: number | null | undefined): string {
    return value === null || value === undefined || !(value > 1) ? 'no limit' : `CHF ${formatChf(value)}`;
}

/**
 * E42: the hint of a row whose first nightly run after the youngest written / manual request shows another value than
 * that request; null when there is no such request or the values agree. The nightly value is the limit of the row; the
 * hint changes no figure.
 */
export function nightlyDiffersText(row: OverviewRow): string | null {
    const d = row.nightlyDiffers;
    if (!d) return null;
    const how = d.mode === 'manual' ? 'entered in NAV by hand' : 'written to NAV';
    return `The nightly run shows ${limitText(row.nightlyLimitChf)}, not the ${limitText(d.requestedLimitChf)} of request #${d.requestId} (${how} on ${formatSwissDate(d.writtenAt)}).`;
}

/** C6, E42: the visible word next to the limit and the full hint for the tooltip and a screen reader. */
function LimitNote({ word, text }: { word: string; text: string }) {
    return (
        <>
            <span className="lim-val muted" aria-hidden="true">{`· ${word}`}</span>
            <span className="sr-only">{text}</span>
        </>
    );
}

/**
 * The limit cell's content with a hint: «unconfirmed» while a written limit waits for its first nightly run (C6),
 * «differs» when that run shows another value than the request (E42).
 */
function withLimitNote(row: OverviewRow, content: ReactNode): ReactNode {
    const pending = pendingNavWriteText(row);
    if (pending !== null) {
        return (
            <span className="lim-cell" title={pending} data-pending-nav-write={row.pendingNavWrite?.mode}>
                {content}
                <LimitNote word="unconfirmed" text={pending} />
            </span>
        );
    }
    const differs = nightlyDiffersText(row);
    if (differs === null) return content;
    return (
        <span className="lim-cell" title={differs} data-nightly-differs={row.nightlyDiffers?.requestId}>
            {content}
            <LimitNote word="differs" text={differs} />
        </span>
    );
}

/**
 * The status for the last column: the open request; without one a written limit the nightly run has not confirmed yet
 * (C6); null when there is neither (a dash).
 */
export function requestStatusText(row: OverviewRow): string | null {
    const r = row.openRequest;
    if (!r) {
        const p = row.pendingNavWrite;
        if (!p) return null;
        return `${p.mode === 'manual' ? 'Entered in NAV by hand' : 'Written to NAV'} · unconfirmed`;
    }
    const status = REQUEST_STATUS[r.status] ?? r.status;
    const who = r.status === 'pending' && r.nextApproverRole ? REQUEST_ROLE[r.nextApproverRole] ?? r.nextApproverRole : null;
    return who ? `${status} · ${who}` : status;
}

function customerName(row: OverviewRow): string {
    return row.customerName?.trim() || row.customerCode;
}

/** Overdue cell: the amount, with the days past due and a dot when E16 marks it. */
function OverdueCell({ row }: { row: OverviewRow }) {
    if (row.overdueMark === null) return <>{row.overdueNetChf === 0 ? '–' : formatChf(row.overdueNetChf)}</>;
    return (
        <span className={`mark ${row.overdueMark}`} title={`Oldest open item ${row.overdueDays ?? 0} days past due`}>
            <span className="sr-only">{row.overdueMark === 'red' ? 'Overdue, red: ' : 'Overdue, amber: '}</span>
            {formatChf(row.overdueNetChf)}
            <span className="muted">{row.overdueDays !== null ? ` · ${row.overdueDays} d` : ''}</span>
        </span>
    );
}

/**
 * Utilization and preview as a badge (E30): an icon and the band's words for a screen reader and for anyone who does not
 * tell red from green, a small meter capped at 100 %, and the real figure. «No limit» is plain grey text.
 */
export function LimitBadge({ ratio, band, what }: { ratio: number | null; band: LimitBand; what: string }) {
    if (band === 'none') return <span className="muted">No limit</span>;
    const width = meterPercent(ratio);
    return (
        <span className={`lim-badge ${band}`} title={`${what}: ${BAND_TEXT[band]}`}>
            {band === 'over' ? <AlertOctagon className="i sm" aria-hidden="true" /> : null}
            {band === 'near' ? <AlertTriangle className="i sm" aria-hidden="true" /> : null}
            <span className="sr-only">{`${BAND_TEXT[band]}: `}</span>
            <span className="lim-meter" aria-hidden="true"><span className="lim-meter-fill" style={{ width: `${width}%` }} /></span>
            <span className="lim-val">{formatPercent(ratio)}</span>
        </span>
    );
}

/**
 * The cell edit of the limit column (B4). Without it the column is read-only, exactly as in A7.
 */
export interface LimitEditColumn {
    /** E7 + E11: may the signed-in person edit this row's limit now? */
    editable: (row: OverviewRow) => boolean;
    /** The cell is locked by an open request although the person has a role (tooltip only, no marker). */
    locked: (row: OverviewRow) => boolean;
    /** The pencil: open the editor. */
    onEdit: (row: OverviewRow) => void;
    /** The open editor of the row. */
    renderEditor: (row: OverviewRow) => ReactNode;
}

/**
 * The limit column; with `edit` the cell carries the edit marker and the editor slot (B4). It shows the effective limit
 * (C6): a limit written into NAV that the nightly run has not confirmed yet carries «· unconfirmed» and the hint as
 * tooltip and screen-reader text (`pendingNavWriteText`); after that run a different nightly value carries «· differs»
 * (`nightlyDiffersText`, E42). Neither changes a figure.
 */
function limitColumn(edit?: LimitEditColumn): ClimColumn<OverviewRow> {
    const base: ClimColumn<OverviewRow> = {
        key: 'limit', label: 'Limit CHF',
        title: 'Credit limit in CHF; «unconfirmed» = written to NAV, the nightly run has not confirmed it yet; «differs» = the nightly run shows another value than the last written request', width: 150, numeric: true,
        value: r => (r.hasLimit ? r.creditLimitChf : null),
        render: r => withLimitNote(r, r.hasLimit ? formatChf(r.creditLimitChf) : <span className="muted">No limit</span>),
    };
    if (!edit) return base;
    return {
        ...base,
        width: 170,
        render: r => withLimitNote(r, <LimitCellView row={r} editable={edit.editable(r)} locked={edit.locked(r)} onEdit={edit.onEdit} />),
        cellClassName: r => (edit.editable(r) ? 'lim-editable' : undefined),
        editable: edit.editable,
        renderEditor: edit.renderEditor,
    };
}

/** The columns for one near-limit threshold (E30; the config's `NearLimitPct`), with the limit edit when given (B4). */
export function overviewColumns(nearLimitPct: number, edit?: LimitEditColumn): readonly ClimColumn<OverviewRow>[] {
    return [
        {
            key: 'customer', label: 'Customer', width: 280, isKey: true, hideable: false,
            value: customerName,
            render: r => (
                <span className="rowkey">
                    <span className="name">{customerName(r)}</span>
                    <span className="meta">{r.customerCode}</span>
                </span>
            ),
        },
        { key: 'salesperson', label: 'Salesperson', width: 170, value: r => r.salesManagerName },
        { key: 'terms', label: 'Payment terms', title: 'Payment terms code', width: 130, value: r => r.paymentTermCode },
        limitColumn(edit),
        {
            key: 'insurance', label: 'Insurance EUR', title: 'Credit insurance limit in EUR', width: 130, numeric: true,
            value: r => r.creditInsuranceEur,
            render: r => formatChf(r.creditInsuranceEur),
        },
        { key: 'balance', label: 'Balance CHF', title: 'Open balance in CHF', width: 130, numeric: true, value: r => r.balanceChf, render: r => formatChf(r.balanceChf) },
        {
            key: 'overdue', label: 'Overdue CHF', title: 'Net overdue amount in CHF; dot = oldest open item past due (red or amber)', width: 160, numeric: true,
            value: r => r.overdueNetChf,
            render: r => <OverdueCell row={r} />,
        },
        {
            key: 'utilization', label: 'Utilization', title: `Balance / limit; red over 100 %, amber from ${nearLimitPct} %`, width: 160, numeric: true,
            value: r => percentValue(r.utilization),
            render: r => <LimitBadge ratio={r.utilization} band={utilizationBand(r, nearLimitPct)} what="Utilization" />,
        },
        {
            key: 'preview', label: 'Preview', title: `(Balance + open sales orders + open frame orders) / limit; red over 100 %, amber from ${nearLimitPct} %`, width: 160, numeric: true,
            value: r => percentValue(r.preview),
            render: r => <LimitBadge ratio={r.preview} band={previewBand(r, nearLimitPct)} what="Preview" />,
        },
        {
            key: 'request', label: 'Last request',
            title: 'Status of the open limit request; without one, a limit written to NAV that the nightly run has not confirmed yet', width: 190,
            value: r => requestStatusText(r),
        },
    ];
}

/** The columns at the default threshold (80 %, E30). */
export const OVERVIEW_COLUMNS: readonly ClimColumn<OverviewRow>[] = overviewColumns(DEFAULT_NEAR_LIMIT_PCT);

export interface OverviewMenuActions {
    invoices: (row: OverviewRow) => void;
    orders: (row: OverviewRow) => void;
    sales: (row: OverviewRow) => void;
    comments: (row: OverviewRow) => void;
    email: (row: OverviewRow) => void;
    /**
     * E21: may the person mail the salesperson about this row? The same rule as writing comments
     * (`canWriteComments`); the server refuses everybody else (`credit-limits-send-email`). Without it the entry shows.
     */
    canEmail?: (row: OverviewRow) => boolean;
}

/**
 * The right-click menu of a row (E15): invoices, orders, sales, comments, and the mail to the salesperson only for a
 * person who may write (E21).
 */
export function buildOverviewMenu(row: OverviewRow, a: OverviewMenuActions): ClimMenuItem[] {
    const items: ClimMenuItem[] = [
        { label: 'Open invoices', onClick: () => a.invoices(row) },
        { label: 'Open orders', onClick: () => a.orders(row) },
        { label: 'Sales (12 months)', onClick: () => a.sales(row) },
        { label: 'Comments', onClick: () => a.comments(row) },
    ];
    if (!a.canEmail || a.canEmail(row)) items.push({ label: 'Email to salesperson', onClick: () => a.email(row) });
    return items;
}

/** Whether the person may add or change comments on this customer: finance, its salesperson or its BT manager (E21). */
export function canWriteComments(
    row: OverviewRow,
    roles: { finance: boolean; smFor: string[]; btmFor: string[] } | null,
): boolean {
    if (!roles) return false;
    if (roles.finance) return true;
    const sm = row.salesManagerName;
    return sm !== null && (roles.smFor.includes(sm) || roles.btmFor.includes(sm));
}
